// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Mail, MapPin, Send, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const contactSchema = z.object({
  name: z.string()
    .trim()
    .min(1, { message: "Name is required" })
    .max(100, { message: "Name must be less than 100 characters" }),
  email: z.string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  phone: z.string()
    .trim()
    .max(20, { message: "Phone must be less than 20 characters" })
    .optional(),
  subject: z.string()
    .trim()
    .min(1, { message: "Subject is required" })
    .max(200, { message: "Subject must be less than 200 characters" }),
  message: z.string()
    .trim()
    .min(1, { message: "Message is required" })
    .max(2000, { message: "Message must be less than 2000 characters" })
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    }
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    
    try {
      const emailContent = `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #002E62, #BC8D09); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
    <h1 style="color: white; margin: 0; font-size: 28px;">New Contact Form Submission</h1>
    <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">RecruitLook Contact Form</p>
  </div>
  
  <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px;">
    <div style="background: white; padding: 25px; border-radius: 8px; border-left: 4px solid #002E62; margin-bottom: 25px;">
      <h3 style="color: #002E62; margin-top: 0; margin-bottom: 20px; font-size: 20px;">Contact Details</h3>
      
      <div style="margin-bottom: 15px;">
        <strong style="color: #002E62;">Name:</strong> 
        <span style="font-weight: bold;">${data.name}</span>
      </div>
      
      <div style="margin-bottom: 15px;">
        <strong style="color: #002E62;">Email:</strong> 
        <span style="font-weight: bold;">${data.email}</span>
      </div>
      
      ${data.phone ? `
      <div style="margin-bottom: 15px;">
        <strong style="color: #002E62;">Phone:</strong> 
        <span style="font-weight: bold;">${data.phone}</span>
      </div>
      ` : ''}
      
      <div style="margin-bottom: 0;">
        <strong style="color: #002E62;">Subject:</strong> 
        <span style="font-weight: bold;">${data.subject}</span>
      </div>
    </div>
    
    <div style="background: white; padding: 25px; border-radius: 8px; border-left: 4px solid #BC8D09;">
      <h3 style="color: #002E62; margin-top: 0; margin-bottom: 15px; font-size: 18px;">Message</h3>
      <p style="color: #333; line-height: 1.6; margin: 0; white-space: pre-wrap;">${data.message}</p>
    </div>
    
    <p style="font-size: 14px; color: #666; margin-top: 30px; margin-bottom: 0; text-align: center;">
      Submitted on ${new Date().toLocaleString('en-US', { 
        dateStyle: 'long', 
        timeStyle: 'short',
        timeZone: 'America/New_York'
      })} EST
    </p>
  </div>
</div>
      `.trim();

      // Send to both email addresses
      const recipients = ['nancy@zw2.com', 'mike@recruitlook.com'];
      
      const insertPromises = recipients.map(recipient =>
        supabase.from('communication_logs').insert({
          type: 'email',
          recipient_email: recipient,
          subject: `Contact Form: ${data.subject}`,
          content: emailContent,
          status: 'pending',
          metadata: {
            trigger_source: 'contact_form',
            sender_name: data.name,
            sender_email: data.email,
            sender_phone: data.phone || null,
            form_subject: data.subject
          }
        })
      );

      const results = await Promise.all(insertPromises);
      
      // Check if any inserts failed
      const hasError = results.some(result => result.error);
      
      if (hasError) {
        throw new Error('Failed to send contact form');
      }

      toast.success('Message sent successfully!', {
        description: 'We\'ll get back to you as soon as possible.'
      });
      
      form.reset();
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or email us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary-dark text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-black mb-4">
              GET IN TOUCH
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Have questions about RecruitLook? We're here to help. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-primary">Send Us a Message</CardTitle>
                <CardDescription>Fill out the form below and we'll get back to you shortly</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Your full name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email *</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="your.email@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone (Optional)</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="(555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subject *</FormLabel>
                          <FormControl>
                            <Input placeholder="What is this regarding?" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message *</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Tell us more about your inquiry..." 
                              rows={6}
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      className="w-full bg-primary hover:bg-primary-dark font-black"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl font-black text-primary">Contact Information</CardTitle>
                  <CardDescription>Reach out to us directly</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Email</h3>
                      <a href="mailto:info@recruitlook.com" className="text-primary hover:underline">
                        info@recruitlook.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <MessageSquare className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Text Only</h3>
                      <a href="sms:+19133694667" className="text-primary hover:underline">
                        #913-369-HOOP (4667)
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Office</h3>
                      <p className="text-gray-600">
                        PO Box 8482<br />
                        Kansas City, MO. 64114<br />
                        United States
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg bg-gradient-to-br from-primary to-primary-dark text-white">
                <CardContent className="pt-6">
                  <h3 className="font-black text-2xl mb-3">Business Hours</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>M-F:</span>
                      <span className="font-bold">9a-4p CST</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sat:</span>
                      <span className="font-bold">Closed</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sun:</span>
                      <span className="font-bold">Closed</span>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-gray-200">
                    We typically respond within 24 hours during business days.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Quick answers to common questions
            </p>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>How quickly will I receive a response?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  We aim to respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What information should I include in my message?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Please include as much detail as possible about your inquiry, including your name, contact information, and a clear description of what you need help with.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Do you offer phone consultations?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Yes! After submitting your inquiry, we can schedule a phone consultation to discuss your needs in more detail.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;